import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Draggable, Droppable, DraggableChildrenFn } from 'react-beautiful-dnd'
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill'
import patchDraggableStyle from './patchDraggableStyle'
import { useDragDropHandler } from './DragDropContext'
import { bindMouseDownEvents, getMousePosition } from '../../utils/mouseUtils'
import { createId } from '../../utils/createId'

export type CloneDraggableProps = {
  draggableId: string
  className?: string
  renderClone?: () => JSX.Element
  type?: string
  enabled?: boolean
}

const DragHandle = styled.div`
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const DragHandleInner = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const FullSize = styled.div`
  width: 100%;
  height: 100%;
`

const MeasureClone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
`

const CloneDraggable = styled<React.FC<CloneDraggableProps>>(
  ({ draggableId, className, renderClone, type, children, enabled = true }) => {
    const droppableId = useMemo(() => `disabled-droppable-${createId()}`, [])
    const itemSizeRef = useRef<HTMLDivElement>(null)
    const itemSize = useResizeDetector({ targetRef: itemSizeRef })
    const clone = useRef<HTMLDivElement>(null)

    const renderItem = useCallback<DraggableChildrenFn>(
      (provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.dragHandleProps}
          {...provided.draggableProps}
          style={patchDraggableStyle(provided.draggableProps.style, snapshot)}
        >
          {renderClone ? renderClone() : children}
        </div>
      ),
      [children, renderClone]
    )

    const dragHandleInnerRef = useRef<HTMLDivElement>(null)

    const placeDraggable = useCallback(
      (mouseX: number, mouseY: number) => {
        const el = dragHandleInnerRef.current
        const wrap = itemSize.ref.current
        if (!el || !wrap || !clone.current || !itemSizeRef.current) {
          return
        }
        const { left: containerLeft, top: containerTop } = itemSizeRef.current.getBoundingClientRect()
        const { width = 0, height = 0 } = clone.current.getBoundingClientRect()
        el.style.position = 'fixed'
        el.style.width = width + 'px'
        el.style.height = height + 'px'
        el.style.left = mouseX - containerLeft - width / 2 + 'px'
        el.style.top = mouseY - containerTop - height / 2 + 'px'
      },
      [clone, itemSize.ref]
    )
    const resetDraggable = useCallback(() => {
      const el = dragHandleInnerRef.current
      if (!el) {
        return
      }
      el.style.position = ''
      el.style.width = ''
      el.style.height = ''
      el.style.left = ''
      el.style.top = ''
    }, [])

    useDragDropHandler(
      useMemo(
        () => ({
          onDragEnd: resetDraggable
        }),
        [resetDraggable]
      )
    )

    const mouseDownEvents = useMemo(
      () =>
        bindMouseDownEvents((e) => {
          placeDraggable(...getMousePosition(e))
        }),
      [placeDraggable]
    )

    const id = useMemo(() => createId(), [])

    return (
      <div className={className} ref={itemSizeRef} {...mouseDownEvents} onClick={resetDraggable}>
        {children}
        <MeasureClone ref={clone}>{renderClone ? renderClone() : children}</MeasureClone>
        <DragHandle>
          <DragHandleInner ref={dragHandleInnerRef}>
            <Droppable droppableId={droppableId} isDropDisabled type={type} renderClone={renderItem}>
              {(provided) => (
                <FullSize ref={provided.innerRef} {...provided.droppableProps}>
                  <Draggable draggableId={draggableId + '#' + id} index={0} isDragDisabled={!enabled}>
                    {(provided) => (
                      <FullSize {...provided.dragHandleProps} ref={provided.innerRef} {...provided.draggableProps} />
                    )}
                  </Draggable>
                  <div style={{ display: 'none' }}>{provided.placeholder}</div>
                </FullSize>
              )}
            </Droppable>
          </DragHandleInner>
        </DragHandle>
      </div>
    )
  }
)`
  position: relative;
  transform: translateZ(1px);
`

export default CloneDraggable
